import type { Board, Pos } from "./types";
import { boardDims } from "./board";
import { findDirectMatchPair } from "./matching";
import { getGroup, getMaxSteps, applySlideMove, findGrabbedMatch } from "./slide";

// 提示结果的三种形态：
//  - pair : 有可直接消除的一对，高亮这两块
//  - slide: 没有直接对，但存在有效滑动；a 是需要抓起推动的方块（滑动前位置），
//           b 是它滑到位后会配对的静止方块 —— 两者即「滑动后可消除的一对」。
//  - none : 无解（调用方应触发洗牌）
export type Hint =
  | { type: "pair"; a: Pos; b: Pos }
  | { type: "slide"; a: Pos; b: Pos; group: Pos[]; dx: number; dy: number; steps: number; desc: string }
  | { type: "none" };

const SLIDE_DIRS: ReadonlyArray<[number, number, string]> = [
  [1, 0, "向右滑动"],
  [-1, 0, "向左滑动"],
  [0, 1, "向下滑动"],
  [0, -1, "向上滑动"],
];

// 智能提示：优先给「直接消除对」；没有则找第一个有效滑动并给出方向建议。
export function computeHint(board: Board): Hint {
  const direct = findDirectMatchPair(board);
  if (direct) return { type: "pair", a: direct.a, b: direct.b };

  const { w, h } = boardDims(board);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (board[y][x] === null) continue;
      for (const [dx, dy, label] of SLIDE_DIRS) {
        const group = getGroup(x, y, dx, dy, board);
        if (!group.length) continue;
        const maxSteps = getMaxSteps(group, dx, dy, board);
        for (let s = 1; s <= maxSteps; s++) {
          const moved = applySlideMove(board, group, dx * s, dy * s);
          const match = findGrabbedMatch(moved, x + dx * s, y + dy * s);
          if (match) {
            // a = 抓起的方块滑动前的位置（group[0]）；b = 配对的静止方块。
            // 由于直接可消除对已在上面优先返回，match.b 必然是不随组移动的方块，
            // 其滑动前后位置一致，可直接用于高亮。
            return { type: "slide", a: { x, y }, b: match.b, group, dx, dy, steps: s, desc: `${label}这组` };
          }
        }
      }
    }
  }
  return { type: "none" };
}
